import { useState, useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Activity, Info } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useTheme } from 'next-themes';
import { SEVERITY_COLORS } from './distributionUtils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

interface ComponentHeatMapData {
  name: string;
  version?: string;
  licensescore?: number;
  vulnerabilityscore?: number;
  technicaldebtscore?: number;
  overallscore?: number;
  operationalriskscore?: number;
}

interface ComponentsHeatMapProps {
  data: ComponentHeatMapData[];
  height?: number;
}

type ScoreType = 'licensescore' | 'vulnerabilityscore' | 'technicaldebtscore' | 'overallscore' | 'operationalriskscore';

const SCORE_OPTIONS = [
  { value: 'overallscore', label: 'Overall Risk Score' },
  { value: 'vulnerabilityscore', label: 'Vulnerability Score' },
  { value: 'licensescore', label: 'License Score' },
  { value: 'technicaldebtscore', label: 'Technical Debt' },
  { value: 'operationalriskscore', label: 'Operational Score' },
];

export const ComponentRiskHeatMap: React.FC<ComponentsHeatMapProps> = ({
  data,
  height = 400
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [selectedScore, setSelectedScore] = useState<ScoreType>('overallscore');
  const { theme } = useTheme();

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    chartInstance.current = echarts.init(chartRef.current, theme === 'dark' ? 'dark' : '');

    const sortedData = [...data]
      .filter(item => item[selectedScore] !== undefined && item[selectedScore] !== null && item[selectedScore] > 0)
      .sort((a, b) => (b[selectedScore] || 0) - (a[selectedScore] || 0))
      .slice(0, 100);

    if (sortedData.length === 0) {
      chartInstance.current.setOption({
        title: {
          text: 'No data available',
          left: 'center',
          top: 'center',
          textStyle: {
            fontSize: 11,
            color: '#000'
          }
        }
      });
      return;
    }

    const scores = sortedData.map(item => item[selectedScore] || 0);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);

    const treemapData = sortedData.map(item => {
      const componentName = item.version ? `${item.name}@${item.version}` : item.name;
      const score = item[selectedScore] || 0;

      return {
        name: componentName,
        value: score,
        score: score,
        itemStyle: {
          borderColor: theme === 'dark' ? '#333' : '#fff',
          borderWidth: 0
        }
      };
    });

    const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        formatter: (params: any) => {
          const scoreLabel = SCORE_OPTIONS.find(opt => opt.value === selectedScore)?.label || 'Score';
          return `<div class="font-medium">${params.name}</div><div>${scoreLabel}: ${Number(params.data.score).toFixed(2)}</div>`;
        }
      },
      series: [{
        name: 'Component Risk',
        type: 'treemap',
        width: '100%',
        height: '100%',
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        label: {
          show: true,
          formatter: (params: any) => {
            return `{name|${params.name}}\n{score|${params.data.score}}`;
          },
          rich: {
            name: {
              fontSize: 10,
              lineHeight: 16,
              color: '#000'
            },
            score: {
              fontSize: 12,
              fontWeight: 'bold',
              color: '#000'
            }
          }
        },
        upperLabel: {
          show: false
        },
        itemStyle: {
          borderColor: theme === 'dark' ? '#333' : '#fff',
          borderWidth: 1,
          gapWidth: 1
        },
        levels: [
          {
            itemStyle: {
              borderWidth: 0,
              gapWidth: 1
            }
          },
          {
            colorSaturation: [0.35, 0.9],
            itemStyle: {
              gapWidth: 1,
              borderColorSaturation: 0.6
            }
          }
        ],
        data: treemapData,
        visualMin: minScore,
        visualMax: maxScore,
        colorMappingBy: 'value',
        visualDimension: 0,
        emphasis: {
          itemStyle: {
            shadowBlur: 4,
            shadowColor: 'rgba(0, 0, 0, 0.7)',
            borderWidth: 1
          }
        }
      }],
      visualMap: {
        show: false,
        min: minScore,
        max: maxScore,
        inRange: {
          color: [
            SEVERITY_COLORS.low,
            SEVERITY_COLORS.medium,
            SEVERITY_COLORS.high,
            SEVERITY_COLORS.critical
          ]
        }
      }
    };

    chartInstance.current.setOption(option);

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, selectedScore, theme]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  return (
    <Card className='relative'>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Risk Heat Map</CardTitle>
            <CardDescription>
              Treemap visualization with size and color representing risk scores across data points
            </CardDescription>
          </div>
          <Select value={selectedScore} onValueChange={(value) => setSelectedScore(value as ScoreType)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select score type" />
            </SelectTrigger>
            <SelectContent>
              {SCORE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <div className="absolute top-2 right-2 z-10">
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="w-4 h-4 text-muted-foreground cursor-help hover:text-foreground transition-colors" />
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="center"
            className="max-w-[300px] text-xs bg-popover text-foreground border border-border shadow-lg"
            sideOffset={8}
          >
            Applications plotted by risk prominence based on component-level analysis across all projects and scans. Higher risk = more prominent placement.
          </TooltipContent>
        </Tooltip>
      </div>
      <CardContent>
        <div style={{ height: `${height}px` }}>
          <div ref={chartRef} className="h-full w-full"></div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComponentRiskHeatMap;